import { createStyles, Group, Stack } from '@mantine/core';
import type { PolicyOut } from '@repo/api-codegen';
import { getBreadCrumbsFromSearchMetadata } from '@repo/common/utils/breadcrumb';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import { capitalize, isEmpty } from 'lodash-es';
import type { ISecodaEntity } from '../../api/types';
import { SecodaEntityIcon } from '../../components/SecodaEntity/SecodaEntityIcon';

const useStyles = createStyles((theme) => ({
	ellipsis: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: theme.other.width.sm,
	},
}));

export function PolicyIssueStatus({ count }: { count?: number }) {
	const displayText =
		count !== undefined
			? `${count} issue${count !== 1 ? 's' : ''}`
			: 'Needs remediation';

	return (
		<Group spacing="3xs">
			<Icon name="alertTriangleFilled" fillColor="icon/warning/default" />
			<Text size="sm">{displayText}</Text>
		</Group>
	);
}

export function PolicyOKStatus() {
	return (
		<Group spacing="3xs">
			<Icon name="circleCheckFilled" fillColor="icon/success/default" />
			<Text size="sm">OK</Text>
		</Group>
	);
}
export function PolicyStatusRender({
	record,
	withCount,
}: {
	record: PolicyOut;
	withCount?: boolean;
}) {
	const resultsCount = record.results_count ?? 0;

	if (resultsCount > 0) {
		return <PolicyIssueStatus count={withCount ? resultsCount : undefined} />;
	}
	return <PolicyOKStatus />;
}

export function PolicySeverityRender({ record }: { record: PolicyOut }) {
	const { severity } = record;

	switch (severity) {
		case 'HIGH':
			return <Icon name="priorityHigh" color="icon/secondary/default" />;
		case 'MEDIUM':
			return <Icon name="priorityMedium" color="icon/secondary/default" />;
		case 'LOW':
			return <Icon name="priorityLow" color="icon/secondary/default" />;
		default:
			return <Icon name="priorityNone" color="icon/secondary/default" />;
	}
}

export function ResourceDisplay({
	record,
	includeLeftSideIcon,
	leftSideIconName,
}: {
	record: Pick<
		ISecodaEntity,
		'title' | 'integration' | 'search_metadata' | 'entity_type' | 'id'
	>;
	includeLeftSideIcon?: boolean;
	leftSideIconName?: IconNames;
}) {
	const { classes } = useStyles();
	const breadcrumbs = getBreadCrumbsFromSearchMetadata(record.search_metadata);

	return (
		<Group spacing="sm" noWrap w="100%">
			{includeLeftSideIcon && leftSideIconName && (
				<Icon name={leftSideIconName} />
			)}
			<SecodaEntityIcon size={28} entity={record} />
			<Stack spacing={0}>
				<Text size="sm" role="link" weight="bold" className={classes.ellipsis}>
					{isEmpty(record.title) ? 'Untitled' : record.title}
				</Text>
				<Group>
					<Text
						size="xs"
						color="text/secondary/default"
						className={classes.ellipsis}
					>
						{capitalize(record.entity_type)} · {breadcrumbs.join(' / ')}
					</Text>
				</Group>
			</Stack>
		</Group>
	);
}
